import {
  Box,
  Dialog,
  DialogProps,
  Divider,
  IconButton,
  Paper,
  Stack,
  useTheme,
} from "@mui/material";
import React from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import { ModalTitleText } from "./DynamicModalHelpers";
import { SSButton, TSSButtonProps } from "./SSButton";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

interface ActionButtonProps {
  variant?: TSSButtonProps["variant"];
  label?: string | React.ReactNode;
  isLoading?: boolean;
  isVisible?: boolean;
  isDisabled?: boolean;
  isHidden?: boolean;
  onClick?: () => void;
  style?: React.CSSProperties;
  fullWidth?: boolean;
  size?: TSSButtonProps["size"];
}

export interface DynamicModalCloseIconProps {
  isHidden?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
  style?: React.CSSProperties;
}

export interface DynamicModalProps {
  maxWidth?: DialogProps["maxWidth"];
  isOpen: boolean;
  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  hideTitleIcon?: boolean;
  hideCloseIcon?: boolean;
  titleIconVariant?: "info" | "warning" | "error" | "success";

  hidePrimaryButton?: boolean;
  hideSecondaryButton?: boolean;
  primaryButtonText?: string | React.ReactNode;
  secondaryButtonText?: string | React.ReactNode;
  onClose?: () => void;

  // render props
  renderHeader?: () => React.ReactNode;
  renderFooter?: () => React.ReactNode;
  renderBody?: () => React.ReactNode;

  slot?: {
    dialog?: Partial<DialogProps>;
    paper?: DialogProps["PaperProps"];
    body?: { style?: React.CSSProperties };
    closeIcon?: DynamicModalCloseIconProps;
    footer?: { style?: React.CSSProperties };
    primaryButton?: ActionButtonProps;
    secondaryButton?: ActionButtonProps;
  };
}

function calcModalWidth(width: DialogProps["maxWidth"]) {
  const defaultWidth = "700px";
  switch (width) {
    case "xs":
      return "600px";
    case "sm":
      return defaultWidth;
    case "md":
      return "900px";
    case "lg":
      return "1200px";
    case "xl":
      return "1486px";
    default:
      return defaultWidth;
  }
}

const renderTitleIcon = (variant: DynamicModalProps["titleIconVariant"]) => {
  switch (variant) {
    case "info":
      return <InfoOutlinedIcon color="info" />;
    case "warning":
      return <WarningAmberRoundedIcon color="warning" />;
    case "error":
      return <HighlightOffRoundedIcon color="error" />;
    case "success":
      return <CheckCircleOutlineRoundedIcon color="success" />;
    default:
      return <InfoOutlinedIcon />;
  }
};

export function DynamicModal(props: DynamicModalProps) {
  const theme = useTheme();
  const { palette } = theme;

  return (
    <Dialog
      open={props.isOpen}
      PaperComponent={Paper}
      fullScreen={props?.slot?.dialog?.fullScreen}
      PaperProps={{
        sx: {
          position: "relative",
          backgroundColor: palette.background.default,
          backgroundImage: "unset",
          border: `1px solid ${palette.divider}`,
          maxWidth: "100%",
          width: calcModalWidth(props.maxWidth),
          borderRadius: "24px",
          ...props?.slot?.paper?.style,
        },
      }}
      style={{
        backdropFilter: "blur(4px)",
        ...props?.slot?.dialog?.style,
      }}
    >
      {!props.hideCloseIcon && !props?.slot?.closeIcon?.isHidden ? (
        <Box
          sx={{
            position: "absolute",
            top: "8px",
            right: "8px",
            zIndex: 3,
          }}
        >
          <IconButton
            size="medium"
            disabled={props?.slot?.closeIcon?.isDisabled}
            onClick={() => {
              if (props?.slot?.closeIcon?.onClick) {
                return props.slot.closeIcon.onClick();
              } else if (props?.onClose) props.onClose();
            }}
            sx={props?.slot?.closeIcon?.style || null}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>
      ) : null}

      {props?.renderHeader?.() || (
        <Stack
          py={2}
          px={3}
          direction={"row"}
          alignItems={"center"}
          spacing={1.2}
        >
          {!props?.hideTitleIcon
            ? renderTitleIcon(props.titleIconVariant)
            : null}
          <ModalTitleText>{props?.title}</ModalTitleText>
        </Stack>
      )}

      <Divider />
      <Box
        sx={{
          maxHeight: "600px",
          overflowY: "auto",
        }}
      >
        {props?.renderBody?.() || <Box p={3}>{props.description}</Box>}
      </Box>
      <Divider />
      {props?.renderFooter ? (
        props.renderFooter()
      ) : (
        <Stack
          direction={"row"}
          justifyContent={"flex-end"}
          py={2}
          px={3}
          rowGap={2}
          columnGap={2}
          flexWrap={"wrap"}
          sx={props?.slot?.footer?.style || null}
        >
          {!props?.slot?.secondaryButton?.isHidden ? (
            <SSButton
              size={props?.slot?.secondaryButton?.size || "large"}
              fullWidth={props?.slot?.secondaryButton?.fullWidth}
              variant={props?.slot?.secondaryButton?.variant || "outlined"}
              onClick={props?.slot?.secondaryButton?.onClick}
              style={{
                minWidth: "120px",
                ...props?.slot?.secondaryButton?.style,
              }}
              loading={props?.slot?.secondaryButton?.isLoading}
              disabled={props?.slot?.secondaryButton?.isDisabled}
            >
              {props?.slot?.secondaryButton?.label || "Close"}
            </SSButton>
          ) : null}
          {!props?.slot?.primaryButton?.isHidden ? (
            <SSButton
              size={props?.slot?.primaryButton?.size || "large"}
              fullWidth={props?.slot?.primaryButton?.fullWidth}
              variant={props?.slot?.primaryButton?.variant || "contained"}
              onClick={props?.slot?.primaryButton?.onClick}
              style={{
                minWidth: "120px",
                ...props?.slot?.primaryButton?.style,
              }}
              loading={props?.slot?.primaryButton?.isLoading}
              disabled={props?.slot?.primaryButton?.isDisabled}
            >
              {props?.slot?.primaryButton?.label || "Confirm"}
            </SSButton>
          ) : null}
        </Stack>
      )}
    </Dialog>
  );
}
